/**
 * Chat, minus the socket.
 *
 * Everything the panel needs that is not a live connection: who this browser
 * is, what it wants to be called, which room it picked and whether the level is
 * allowed to override it, whether the panel was open and where it was dragged
 * to, and where the socket lives.
 *
 * Identity here is deliberately thin, and it is no longer the only kind. A
 * signed-in learner's name, picture and tag come from their account and none
 * of this is load-bearing for them (see `recoverPrefs`). What this file keeps
 * is the other identity: the browser makes itself a random id once and keeps
 * it in localStorage, and the server hashes it into the four-character tag
 * shown beside a typed name. The id never appears in the interface and is
 * never sent anywhere but the socket, which is what makes it safe to hold in
 * plain storage: the worst a copied id can do is let another browser wear the
 * same tag.
 *
 * Where that identity is still what posts, and where it is only what reads,
 * is the room's decision rather than this file's: posting needs an account
 * wherever signing in is possible at all (worker/room.ts, `gated`). On a
 * gated deployment a stored name is what the panel remembers, not what the
 * room accepts, and the id underneath it still earns the tag on a browser
 * that is only listening.
 *
 * Every storage access is wrapped, the way progress.ts wraps its own, because
 * localStorage is absent or throwing in more places than it should be —
 * private windows on some browsers, a document embedded where storage is
 * partitioned away — and none of them is a reason for chat not to work.
 *
 * That thinness is also why the identity is mirrored. There is no account to
 * fall back on when signed out, so one cleared key is a learner who walks back
 * into a room they have been in for weeks wearing a tag nobody recognises, and
 * with no way to prove the old one was theirs. vault.ts keeps spare copies in
 * IndexedDB and the Cache API; every write here goes to both, and recoverPrefs
 * below adopts a spare when — and only when — this browser turns up with no
 * identity of its own. localStorage keeps exactly the string it has always
 * kept, so a build with the vault and a build without it read each other's
 * storage without noticing the difference.
 *
 * Three of the preferences below are about other people rather than about
 * this browser — who it will not read (`blocked`), whether being spoken to
 * may interrupt (`notifyMentions`), and how far down its own mentions it has
 * got (`seenInbox`). They are fields on the same blob rather than a second
 * key, because the blob is already mirrored and already recovered, and a
 * second key would be a second thing to lose. They are also the reason the
 * shape of the blob is added to and never rearranged: `recoverPrefs` adopts a
 * whole copy written by some earlier version of this file, and a field that
 * changed meaning between two of those versions is a field that means the
 * wrong thing on exactly the browser that was wiped (docs/contracts.md, "5.
 * Identity").
 */
import {
  DEFAULT_ROOM,
  GENERAL_TRACK,
  cleanFace,
  cleanId,
  cleanName,
  cleanSeed,
  cleanTag,
  levelRoom,
  parseRoomId,
  type Member,
  type RoomId,
} from '@engineer-os/chat';
import { mirror, mirroredCopies } from './vault';

/** The one localStorage key the chat owns. */
export const CHAT_KEY = 'engineeros.chat.v1';

/** How many rooms the picker offers as shortcuts. */
export const RECENT_MAX = 5;

/** How many tags one browser may block. Far past what anybody needs; see `normalise`. */
export const BLOCKED_MAX = 500;

/**
 * A place to put the panel: how far its edges sit from the bottom **inline-end**
 * corner of the window, in CSS pixels.
 *
 * `end`, not `right`, and the rename is the whole point. The panel is pinned
 * to the corner the chat bubble lives in, and in Arabic that corner is on the
 * left. A distance stored from the right edge would mean one thing while the
 * page was English and a different thing the moment it was not — a panel
 * dragged to sit beside the bubble would jump to the opposite side of the
 * window on the next language switch.
 *
 * Measured from the inline end, the number means "how far from the bubble",
 * which is the same sentence in both languages and needs no conversion when
 * one becomes the other. `cleanDock` migrates the blobs written before this
 * was true; see it for what happens to them.
 */
export interface Dock {
  end: number;
  bottom: number;
}

/** Anything with a width and a height, in CSS pixels. */
export interface Size {
  w: number;
  h: number;
}

export interface ChatPrefs {
  /** Made once, kept for ever, never shown. The tag is derived from it server-side. */
  id: string;
  /** The display name, or null until one has been picked on this browser. */
  name: string | null;
  /** The room picked by hand. What the tab is actually in is {@link roomFor}. */
  room: RoomId;
  /** Rooms joined, most recent first, at most RECENT_MAX. */
  recent: RoomId[];
  /** Whether the drawer was open when the page was last left. */
  open: boolean;
  /** The face picked from the set, or null for the one the tag gives you. */
  face: string | null;
  /**
   * The operator this learner built, or null while they wear the one their id
   * gives them (lib/operator.ts).
   *
   * Here rather than in a key of its own because it is the same kind of fact
   * as the name and the face — what this browser looks like in a room — and
   * because this blob is the one that is mirrored into the vault. A learner
   * whose localStorage is cleared gets their figure back with their tag, and
   * the two were always going to have to survive together: an identity that
   * comes back under a different face is not the identity coming back.
   *
   * A signed-in learner's figure lives on their account as well, and the two
   * are reconciled once per load (lib/operator.ts, `syncOperator`). This is
   * still written for them, so that signing out leaves them looking like
   * themselves.
   */
  seed: string | null;
  /** Whether the room follows the level being played. See {@link roomFor}. */
  follow: boolean;
  /** Where the panel was dragged to, or null while it sits where it was put. */
  dock: Dock | null;
  /** How big it was pulled to, or null while it is the size the stylesheet says. */
  size: Size | null;
  /**
   * Tags this reader does not want to see, newest first.
   *
   * A block is a preference in one browser and never a frame on the wire.
   * What one reader does not want to read is nobody else's business, and
   * certainly not the server's: a server-side block list would be a list of
   * who dislikes whom, kept for ever, on a product that has no use for one.
   * The cost is that it does not follow the learner to another browser, which
   * is the right way round — the person they blocked does not follow them
   * either.
   */
  blocked: string[];
  /**
   * Whether a mention may raise a browser notification. Default false, and
   * deliberately not "ask on first mention": a permission prompt is noise on
   * a page somebody has just opened, and the only honest moment to ask is
   * when they have turned the setting on themselves.
   */
  notifyMentions: boolean;
  /**
   * The highest mention id this browser has acknowledged.
   *
   * The account holds the real answer — `seen` on each row, which is what
   * survives closing the tab — and this is the local half that stops the same
   * mention raising a second notification on this browser after the request
   * that would have marked it seen failed. A number rather than a list
   * because mention ids only go up.
   */
  seenInbox: number;
}

let cache: ChatPrefs | null = null;

/**
 * True when the read below found no usable id in storage and minted one. It is
 * the only state recoverPrefs acts on: a browser that still has an identity
 * keeps it, mirrors or no mirrors.
 */
let mintedFresh = false;

/** True once recoverPrefs has looked at the mirrors, whatever it found there. */
let recoveryDone = false;

/**
 * A fresh browser id. `crypto.randomUUID` is the normal path; the fallback
 * exists for the few contexts that still lack it (an insecure origin, an old
 * WebView) and produces the same shape the protocol's `cleanId` accepts.
 */
function makeId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  const bytes = new Uint8Array(16);
  try {
    crypto.getRandomValues(bytes);
  } catch {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The primary copy, and the spares with it.
 *
 * Both get the identical string: localStorage holds what it has always held,
 * and the vault enveloped it rather than reshaping it, so neither copy is the
 * one that has to be upgraded first. The mirror is fire and forget — a learner
 * pressing Enter waits on a socket, never on IndexedDB.
 */
function write(p: ChatPrefs): void {
  const json = JSON.stringify(p);
  try {
    localStorage.setItem(CHAT_KEY, json);
  } catch {
    /* storage unavailable: the id lives for this page only, which still works */
  }
  // The one write that is held back. A freshly minted id must not reach the
  // mirrors until recoverPrefs has read them, because mirror() overwrites:
  // copying this browser's brand-new identity into the spares would destroy
  // the identity recovery exists to find, in the exact case it is needed.
  if (mintedFresh && !recoveryDone) return;
  mirror(CHAT_KEY, json);
}

/** The stored blob as an object, or null when there is nothing there to read. */
function parseBlob(raw: string | null | undefined): Partial<ChatPrefs> | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<ChatPrefs> | null;
    return typeof value === 'object' && value !== null ? value : null;
  } catch {
    return null;
  }
}

/**
 * A blob held to the protocol's own rules, so a hand-edited one degrades to
 * defaults rather than to a socket the server refuses. Shared by the read and
 * by the recovery: a mirrored copy came out of this browser's own storage,
 * which is to say out of the same hands as a stored one, and it earns exactly
 * the same suspicion. The id is the only field with nothing to fall back to,
 * so the caller settles that one and passes it in.
 */
function normalise(stored: Partial<ChatPrefs> | null, id: string): ChatPrefs {
  const recent = Array.isArray(stored?.recent)
    ? stored.recent.map(parseRoomId).filter((r): r is RoomId => r !== null).slice(0, RECENT_MAX)
    : [];
  return {
    id,
    name: cleanName(stored?.name),
    room: parseRoomId(stored?.room) ?? DEFAULT_ROOM,
    recent,
    open: stored?.open === true,
    // Checked against the protocol's set, so a hand-edited blob cannot put
    // anything but a face on a socket; the room would refuse it anyway.
    face: cleanFace(stored?.face),
    // And the same for the operator: shape only, which is all the wire checks
    // and all this can check — the rails live in lib/avatar.ts and the
    // renderer clamps an index it does not have a part for.
    seed: cleanSeed(stored?.seed),
    // Absent means true: following the level is the default, and a blob
    // written before this field existed belongs to a learner who has never
    // said otherwise.
    follow: stored?.follow !== false,
    dock: cleanDock(stored?.dock),
    size: cleanSize(stored?.size),
    // Held to the protocol's own rule, like everything above it: a tag is
    // four hex characters and a blob that says otherwise is a blob somebody
    // edited. Capped, because the list is read on every message drawn and a
    // hand-written one of ten thousand entries would be a slow panel rather
    // than a refused one.
    blocked: Array.isArray(stored?.blocked)
      ? stored.blocked.map(cleanTag).filter((t): t is string => t !== null).slice(0, BLOCKED_MAX)
      : [],
    // Absent means off. A blob written before this field existed belongs to
    // somebody who has never been asked, and the answer to a question nobody
    // has been asked is no.
    notifyMentions: stored?.notifyMentions === true,
    seenInbox: cleanSeen(stored?.seenInbox),
  };
}

/** A mention id somebody has got as far as, or zero. Ids count up from one, so zero is "none". */
function cleanSeen(raw: unknown): number {
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
}

/**
 * A remembered size, or null for anything that is not two finite numbers.
 *
 * Held to the bounds on the way in as well as on the way out. A blob is
 * something the learner can edit, and a stored width of 40000 would otherwise
 * be a panel that is off the screen until they find the storage inspector.
 */
function cleanSize(raw: unknown): Size | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { w, h } = raw as Partial<Size>;
  if (!Number.isFinite(w) || !Number.isFinite(h)) return null;
  return clampSize({ w: w as number, h: h as number }, CHAT_MAX);
}

/**
 * A remembered position, or null for anything that is not two finite numbers.
 *
 * Also the migration. A blob written before the interface had two languages
 * holds `{ right, bottom }` — a distance from the bottom-right corner. That
 * blob was written by somebody using an English page, because there was no
 * other kind, so its `right` *is* its inline-end distance and the conversion
 * is the identity. It is read once under the old name and returned under the
 * new one; the next `updatePrefs` writes it back in the new shape.
 *
 * Deliberately not a version bump on `CHAT_KEY`. Bumping the key would throw
 * away the learner's name, face, room and recent rooms along with it, to fix
 * one field whose old value is still correct.
 */
function cleanDock(raw: unknown): Dock | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { end, bottom } = raw as Partial<Dock>;
  const legacy = (raw as { right?: unknown }).right;
  const inlineEnd = Number.isFinite(end) ? (end as number) : Number.isFinite(legacy) ? (legacy as number) : undefined;
  if (inlineEnd === undefined || !Number.isFinite(bottom)) return null;
  return { end: inlineEnd, bottom: bottom as number };
}

/**
 * The stored preferences, read once and cached. Every field is validated on
 * the way in with the protocol's own rules, so a hand-edited blob degrades to
 * defaults rather than to a socket the server refuses.
 */
export function chatPrefs(): ChatPrefs {
  if (cache) return cache;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(CHAT_KEY);
  } catch {
    raw = null;
  }
  const stored = parseBlob(raw);
  const kept = cleanId(stored?.id);
  mintedFresh = kept === null;
  cache = normalise(stored, kept ?? makeId());
  if (mintedFresh) {
    // A new id has to be written now, not on the next change, or two page
    // loads in a row would be two different people. This one reaches
    // localStorage only; the mirrors wait for recoverPrefs.
    write(cache);
  } else {
    // A browser that still has its identity refreshes the spares on every
    // load rather than on its next rename. Without this the only browsers
    // with anything to recover would be the ones that happened to change a
    // preference after the vault shipped, and the learner who settles into
    // one room and never touches the picker again — the one whose tag is
    // worth the most to them — would have no copy at all.
    mirror(CHAT_KEY, JSON.stringify(cache));
  }
  return cache;
}

/**
 * True when this page load minted a fresh id because storage had none. The
 * hook asks before deciding whether recovery is worth waiting for, and for
 * every browser that still has an identity the answer is no.
 */
export function looksFresh(): boolean {
  chatPrefs();
  return mintedFresh;
}

/** Merge a change into the preferences and persist it. The id cannot change. */
export function updatePrefs(patch: Partial<Omit<ChatPrefs, 'id'>>): ChatPrefs {
  cache = { ...chatPrefs(), ...patch };
  write(cache);
  return cache;
}

/** `room` moved to the front of the recent list, once, capped. */
export function withRecent(recent: readonly RoomId[], room: RoomId): RoomId[] {
  return [room, ...recent.filter((r) => r !== room)].slice(0, RECENT_MAX);
}

/* ── Which room ─────────────────────────────────────────────────────────── */

/**
 * The room a tab is actually in, which is not always the one it picked.
 *
 * The level wins by default: someone working on Level 7 should be among the
 * people working on Level 7 without having to go and ask for them, and the
 * level list, where there is no level, is the room they chose. Picking a room
 * by hand turns following off and pins that choice through every level after
 * it; the picker's "This level" tag turns it back on. That is the whole rule,
 * and it lives here rather than in the hook so it can be read in one piece.
 *
 * A level with no room of its own falls back to the chosen room. There are a
 * thousand level rooms per track and fifty-two levels, so this is a guard
 * rather than a case — but it is the guard that catches a track this build's
 * protocol cannot spell, and a fall back to the room the learner picked is a
 * much better answer there than the general track's room for the same number,
 * which is a different course's conversation.
 *
 * `track` defaults to the general one, so the callers that have only ever had
 * a level number keep the room they have always been given. It is a parameter
 * rather than a lookup because this function is deliberately arithmetic and
 * not a catalogue reader; the caller that holds the mission passes
 * `mission.track` beside `mission.level` once there is more than one
 * (docs/curriculum.md §6, "Deferred until a second track actually exists").
 */
export function roomFor(chosen: RoomId, follow: boolean, level: number | null, track: string = GENERAL_TRACK): RoomId {
  if (!follow || level === null) return chosen;
  return levelRoom(level, track) ?? chosen;
}

/**
 * Which level a mission id belongs to, or null.
 *
 * Every mission in the catalogue is `l<NN>-<slug>` and has been since the
 * first one; the number in the id is the level, and `scripts/check-missions`
 * is where that would stop being true. This reads it rather than looking the
 * mission up, because the one caller — the cleared-level announcement in
 * useChat — has a `ClearClock`, which carries the id and nothing else, and
 * loading the whole catalogue into the chat to turn one string into one
 * number would put the mission list behind the socket.
 *
 * Null for anything that does not match, and null is the end of it: the
 * announcement is simply not made. A line in a room saying somebody cleared
 * the wrong level is worse than no line, and this is the only place the two
 * could come apart.
 *
 * **Three digits, not two, and the third one was a bug rather than a
 * widening.** `l100-…` did not match, so `missionLevel` answered null and the
 * announcement was never made — silently, for every level from a hundred up,
 * on a course whose plan reaches 99 and then keeps going (docs/curriculum.md
 * §7). Failing safe is the right instinct and this still does it; failing safe
 * at a boundary nobody would cross for another forty levels is how a
 * three-character regex outlives the assumption in it. The bound now comes
 * from `LIMITS.levelMax`, through `levelRoom` below, so the digits here and
 * the range there cannot drift apart again.
 *
 * **What this still assumes, and where it would break.** The number in the id
 * is the *level*, and a level is a position within a track. That holds while
 * the ids are numbered in one sequence, which they are; a catalogue that gave
 * a track its own `l00-` would hand two missions one number, and no function
 * that is only shown an id could tell them apart. That is a property for
 * `scripts/check-missions` to keep rather than one this can check, and it is
 * written down here because this is where it would be noticed last.
 */
export function missionLevel(missionId: string | null | undefined): number | null {
  if (typeof missionId !== 'string') return null;
  const m = /^l(\d{1,3})-/.exec(missionId);
  if (!m) return null;
  const level = Number(m[1]);
  // Through the protocol's own range check, so that a level with no room of
  // its own can never be announced into one that does not exist.
  return levelRoom(level) === null ? null : level;
}

/* ── Where the panel sits, and how big it is ────────────────────────────── */

/** How close to the window's edge the panel may be dragged, in pixels. */
export const DOCK_EDGE = 8;

/**
 * The size the stylesheet draws it at, until the learner pulls it to another
 * one. Kept in step by hand with the fallbacks in `.chat` (chat.css); they are
 * two statements of one number because the stylesheet has to be able to draw
 * the panel before any of this runs, and the handles have to be able to say
 * what their value is before anybody has dragged them.
 */
export const CHAT_DEFAULT: Size = { w: 360, h: 520 };

/**
 * The smallest panel that is still a conversation, and the largest that is
 * still a panel.
 *
 * The floor is the real constraint: under about 280 by 240 the header, the
 * composer and the footer have eaten everything and the messages are a slot
 * two lines high, which is worse than no chat at all. The ceiling is generous
 * on purpose — somebody who wants half their screen to be the room is allowed
 * to have it, and the window is the real limit anyway (see clampSize).
 */
export const CHAT_MIN: Size = { w: 280, h: 240 };
export const CHAT_MAX: Size = { w: 900, h: 1000 };

/**
 * A size, kept sane and kept on screen.
 *
 * `room` is what the panel has to grow into, measured from where it actually
 * is: the panel is anchored to its bottom inline-end corner and grows up and
 * towards the inline start, so the space available is the distance from that
 * corner to the far edges of the window, less a margin.
 *
 * The floor wins over the room, the same way clampDock's margin wins over a
 * window too small to hold the panel. A window shorter than 240px is a window
 * where something has to overflow, and the something is the top of the panel:
 * the composer and the footer are at the bottom, under the learner's hand, and
 * they are what the panel is for.
 */
export function clampSize(size: Size, room: Size, min: Size = CHAT_MIN, max: Size = CHAT_MAX): Size {
  const fit = (want: number, have: number, lo: number, hi: number) =>
    Math.round(Math.min(Math.max(want, lo), Math.max(lo, Math.min(hi, have))));
  return { w: fit(size.w, room.w, min.w, max.w), h: fit(size.h, room.h, min.h, max.h) };
}

/**
 * A position, kept on screen.
 *
 * The position is a distance from the bottom inline-end corner rather than a
 * point,
 * so a window that changes size carries the panel with the corner it was put
 * near instead of leaving it half outside. This handles the one case that
 * cannot fix itself: a window now too small for the offset it was given. The
 * `max` on each far edge is what keeps a window smaller than the panel from
 * producing a negative bound, which would pin the panel off the other side.
 */
export function clampDock(dock: Dock, panel: Size, view: Size, edge = DOCK_EDGE): Dock {
  const far = (viewport: number, size: number) => Math.max(edge, viewport - size - edge);
  return {
    end: Math.min(Math.max(dock.end, edge), far(view.w, panel.w)),
    bottom: Math.min(Math.max(dock.bottom, edge), far(view.h, panel.h)),
  };
}

/* ── Recovery ───────────────────────────────────────────────────────────── */

/**
 * One pass, kept, so that a second caller — a remounted effect in development,
 * or anything else that asks later — gets the answer the first one got instead
 * of a second read of the mirrors.
 */
let recovery: Promise<boolean> | null = null;

/**
 * Take the identity back from the mirrors, if this browser has none of its own.
 *
 * The tag beside a name is a hash of the id in `hello`, so an id lost to a
 * partial wipe of storage is a learner who walks back into their room as a
 * stranger. localStorage is the copy most likely to go alone — a quota
 * eviction takes it first, Safari expires it after seven days away, some
 * browsers empty it on a "clear recent history" — and the spares in IndexedDB
 * and the Cache API usually outlive all three. That asymmetry is the entire
 * trade this function makes; a wipe of everything is still a wipe of
 * everything, and honestly so.
 *
 * It only ever runs one way. Recovery happens when the read above found
 * nothing usable and minted a fresh id, and never when an identity is already
 * in hand, however much newer a mirrored copy may look: silently replacing a
 * working identity is a worse failure than failing to restore a lost one, and
 * it would be the failure that hits every browser rather than the few that
 * were wiped. A mirrored blob is validated exactly as a stored one is, because
 * it is a stored one — the newest copy that parses and carries an acceptable
 * id wins, and an unusable copy is skipped rather than trusted for its age.
 *
 * `room`, `recent`, `open`, `face`, `follow` and `dock` come back with it. A signed-in learner needs
 * none of the identity — the room derives their tag and name from the account
 * and ignores what `hello` claims — but no account has ever held which room
 * they were in, so all of them are worth restoring either way. `open` is put
 * back into the preferences rather than acted on: the drawer's state for this
 * load was settled at the first render, and a panel that slides itself open
 * once the page has already gone quiet is more startling than one that simply
 * comes back on the next visit.
 *
 * Returns true when a copy was adopted, so the caller can pull the recovered
 * room and name into its own state. Never throws: the caller is on the path to
 * a socket, and a browser with a broken IndexedDB should still get chat.
 */
export function recoverPrefs(): Promise<boolean> {
  if (!recovery) recovery = attemptRecovery();
  return recovery;
}

async function attemptRecovery(): Promise<boolean> {
  // Forces the read, which is what decides whether anything is missing.
  chatPrefs();
  if (!mintedFresh) {
    recoveryDone = true;
    return false;
  }

  let found: ChatPrefs | null = null;
  try {
    for (const raw of await mirroredCopies(CHAT_KEY)) {
      const stored = parseBlob(raw);
      const id = cleanId(stored?.id);
      if (!id) continue;
      found = normalise(stored, id);
      break;
    }
  } catch {
    /* no mirrors, or storage that refuses to be read: nothing to recover */
  }

  recoveryDone = true;
  if (found) cache = found;
  // Either way this is the flush that lifts the hold in write(): an adopted
  // copy becomes the primary again, and an identity that turned out to be
  // genuinely new finally reaches the spares, so the next wipe has something
  // to find.
  write(chatPrefs());
  return found !== null;
}

/* ── Wire ───────────────────────────────────────────────────────────────── */

/**
 * Where a room's socket lives.
 *
 * Same origin by default: in production the Worker that serves this page
 * also hosts the rooms, and in development vite proxies `/api/chat` to a
 * local `wrangler dev`. `VITE_CHAT_ORIGIN` (an https origin, no trailing
 * slash) points a static build at a Worker deployed somewhere else.
 */
export function chatUrl(path: string): URL {
  const configured = (import.meta.env.VITE_CHAT_ORIGIN as string | undefined)?.trim().replace(/\/+$/, '');
  const base = configured || window.location.origin;
  return new URL(path, base);
}

export function chatSocketUrl(room: RoomId): string {
  const url = chatUrl(`/api/chat/rooms/${encodeURIComponent(room)}/ws`);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.href;
}

/* ── Permalinks ─────────────────────────────────────────────────────────── */

/**
 * A link to one message, and the two halves of reading one back.
 *
 * A link has to survive a page load or it is not a link, so it lives in the
 * URL and nowhere else. It is read the way `?lang` and `?theme` are read
 * (lib/locale.ts, lib/theme.ts): straight off `window.location.search` by
 * whoever needs the answer, rather than routed through App.tsx. That is not
 * only a convenience — the route in App is the *level*, and a chat permalink
 * is not a place in the course, so making it a route would mean the level
 * list and a message in room 7 were two values of one thing.
 *
 * `chat` is the room and `m` is the message. Two parameters rather than one
 * because a room id is already a string with a dash in it, and a composite
 * would need escaping rules that a learner pasting a link into a message
 * would sooner or later break.
 */
const LINK_ROOM = 'chat';
const LINK_MESSAGE = 'm';

/**
 * The URL that reopens this message, with everything already in the address
 * bar left where it is.
 *
 * `?lang` and `?level` are the two that matter: a link copied out of an
 * Arabic page opens in Arabic, and a link copied while standing in a level
 * opens there. Anything else the URL happens to carry is somebody else's and
 * is preserved for the same reason.
 */
export function chatLink(room: RoomId, id: number): string {
  const url = new URL(window.location.href);
  url.searchParams.set(LINK_ROOM, room);
  url.searchParams.set(LINK_MESSAGE, String(id));
  return url.href;
}

/** The message named in the address bar, or null — including for a link that is not one. */
export function linkedMessage(): { room: RoomId; id: number } | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const room = parseRoomId(params.get(LINK_ROOM));
    const id = Number(params.get(LINK_MESSAGE));
    if (!room || !Number.isInteger(id) || id <= 0) return null;
    return { room, id };
  } catch {
    // A document with no usable location — an embed, a test environment.
    // There is no link to follow, which is the same answer as a bad one.
    return null;
  }
}

/**
 * Take the link back out of the URL once it has been acted on.
 *
 * `replaceState`, so it leaves no entry to go back to, and done as soon as
 * the room has been asked for the page rather than when it arrives: the point
 * is that a refresh is a refresh and not a second navigation to the same
 * message, and that a learner who reads on for ten minutes is not still
 * holding a URL that snaps them back to where they came in.
 */
export function clearLink(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(LINK_ROOM) && !url.searchParams.has(LINK_MESSAGE)) return;
    url.searchParams.delete(LINK_ROOM);
    url.searchParams.delete(LINK_MESSAGE);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* a history API that refuses: the link stays in the bar, which is untidy and nothing worse */
  }
}

/* ── Where everybody is ─────────────────────────────────────────────────── */

/** One room as the index remembers it: how many people are in it, and when it last said anything. */
export interface Occupancy {
  here: number;
  lastTs: number;
}

/**
 * Every room the index has heard from, keyed by room id.
 *
 * This is the empty-room fix, and it is worth saying what the fix is: a
 * learner who types a room number into silence and gets silence back
 * concludes that the chat is dead, when what actually happened is that they
 * picked one of ninety-nine rooms at random. Answering "Room 4 · empty, quiet
 * 3 days" before they pick costs one cached request and turns a dead product
 * into a directory.
 *
 * A room the index has never heard from is absent rather than zero, and the
 * two mean different things: absent is "nothing known", which the picker
 * draws as empty and quiet, and zero is "nobody there right now", which it
 * can draw alongside a last-said time. Neither is an error.
 *
 * Returns an empty map for anything that goes wrong — a Worker that is not
 * there, a route that does not exist yet, a body that is not what we expect.
 * The app runs unchanged when the Worker is unreachable; that is a rule of
 * this codebase and not a special case for this route (docs/contracts.md,
 * "The chat").
 */
export async function fetchOccupancy(): Promise<Record<string, Occupancy>> {
  const out: Record<string, Occupancy> = {};
  try {
    const r = await fetch(chatUrl('/api/chat/rooms').href, { credentials: 'include' });
    if (!r.ok) return out;
    const body = (await r.json()) as { rooms?: unknown };
    if (!Array.isArray(body.rooms)) return out;
    for (const raw of body.rooms) {
      if (typeof raw !== 'object' || raw === null) continue;
      const { id, here, lastTs } = raw as { id?: unknown; here?: unknown; lastTs?: unknown };
      const room = parseRoomId(id);
      if (!room) continue;
      out[room] = {
        here: typeof here === 'number' && here > 0 ? Math.floor(here) : 0,
        lastTs: typeof lastTs === 'number' && lastTs > 0 ? Math.floor(lastTs) : 0,
      };
    }
  } catch {
    /* no Worker, no index, no route yet: nothing known, which the picker can say */
  }
  return out;
}

let nonceSeq = 0;

/**
 * A tag for one `say`, echoed back on the sender's own copy of the message
 * so the optimistic row can be reconciled or marked failed. Unique within a
 * page is all it has to be; the counter guarantees that even if Math.random
 * repeats.
 */
export function makeNonce(): string {
  nonceSeq += 1;
  return `${Date.now().toString(36)}-${nonceSeq.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/* ── Time ───────────────────────────────────────────────────────────────── */

/*
 * Only the calendar-day comparison lives here now.
 *
 * Saying a time used to be this file's job, in a hand-rolled `HH:MM` and an
 * `Intl` call that took whatever language the browser felt like. Both moved to
 * lib/format.ts when the interface gained a second one: the page's language is
 * not the browser's, the digits are a decision rather than a default (see the
 * argument in that file), and a chat column that is five characters wide in
 * one numbering system and not in another is a thread that breathes in and out
 * down its own length.
 *
 * What stays is the question that is not about words at all: whether two
 * instants fall on the same date where the reader is standing.
 */

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Same calendar day in the viewer's own time zone. */
export function sameDay(a: number, b: number): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

/* ── Meta ───────────────────────────────────────────────────────────────── */

/**
 * "L07 · Junior Engineer": the level someone was on and the rank they held,
 * as a message or a member row shows them. Either half can be missing — on
 * the level list there is no level, and a fresh browser has no rank the
 * server would keep — and a missing half is omitted rather than written as
 * a dash, so the line never says less than it knows and never pads.
 */
export function metaFor(level: number | null, rank: string | null): string {
  const parts: string[] = [];
  if (level !== null) parts.push(`L${pad2(level)}`);
  if (rank) parts.push(rank);
  return parts.join(' · ');
}

/* ── Who somebody is ────────────────────────────────────────────────────── */

/** One person as the card shows them, and how current that is. */
export interface Whois {
  /** Everything the panel has about them. */
  member: Member;
  /**
   * True when this came out of the member list — so it is what they are
   * doing now, and not what they were doing when they wrote something.
   */
  here: boolean;
}

/**
 * Who the person behind a tag is, for the card that opens when a name is
 * clicked.
 *
 * Two sources, and the order between them is the whole of this function. The
 * member list is live: it is rewritten on every join, leave and rename, it
 * carries the objective somebody is standing in, and it is what the room
 * would say about them if asked this second. A message carries its own
 * snapshot of the sender, taken when it was written and never updated since.
 * So the list wins wherever it has an answer, and the snapshot is the fallback
 * rather than the other way around — a card that said "Level 3" over a message
 * from an hour ago about somebody now on Level 9 would be a card made of
 * stale facts, which is worse than a card that says less.
 *
 * The fallback is not a courtesy. Most of the names on screen at any moment
 * belong to people who have closed the tab: a room keeps a thousand messages
 * and a member list holds whoever is connected right now, so clicking a name
 * in yesterday's conversation is the common case and not the corner. `here`
 * is what the card says about the difference, and it is the honest half of
 * this feature — the panel knows when it is showing a person and when it is
 * showing a record of one.
 *
 * Null only when there is neither, which is a name that was never on a message
 * and is not in the room. No caller can produce that today; it is here so that
 * one which could would get a card that does not open rather than a card full
 * of blanks.
 */
export function whoIs(tag: string, members: readonly Member[], remembered: Member | null): Whois | null {
  const live = members.find((m) => m.tag === tag);
  if (live) return { member: live, here: true };
  // Only the snapshot that is actually about this tag. A caller holding the
  // wrong row is a bug, and showing its name under this tag would hide it.
  if (remembered && remembered.tag === tag) return { member: remembered, here: false };
  return null;
}
